import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { ChevronRight, MapPin, Calendar, Clock, Trophy, Sparkles, BarChart3, Globe, Zap } from 'lucide-react';
import WCInternalLinks from '@/components/worldcup/WCInternalLinks';
import { translateCountryName, translateGroupName, extractWCMatchId, MARKET_LABELS_TR, SELECTION_LABELS_TR } from '@/lib/wcTranslations';
import WCSubmenu from '@/components/worldcup/WCSubmenu';

const API_BASE = '';

function _teamSlug(name) {
  const map = {
    'Türkiye': 'turkiye', 'Brazil': 'brazil', 'France': 'france', 'Argentina': 'argentina',
    'England': 'england', 'Germany': 'germany', 'Spain': 'spain', 'Portugal': 'portugal',
    'Netherlands': 'netherlands', 'Croatia': 'croatia', 'Belgium': 'belgium', 'USA': 'usa',
    'Mexico': 'mexico', 'Canada': 'canada', 'Japan': 'japan', 'South Korea': 'south-korea',
    'Australia': 'australia', 'Morocco': 'morocco', 'Senegal': 'senegal', 'Uruguay': 'uruguay',
    'Colombia': 'colombia', 'Ecuador': 'ecuador', 'Paraguay': 'paraguay',
    'Switzerland': 'switzerland', 'Austria': 'austria', 'Sweden': 'sweden', 'Norway': 'norway',
    'Scotland': 'scotland', 'Iran': 'iran', 'Saudi Arabia': 'saudi-arabia', 'Qatar': 'qatar',
    'Egypt': 'egypt', 'Tunisia': 'tunisia', 'Algeria': 'algeria', 'Ivory Coast': 'ivory-coast',
    'Ghana': 'ghana', 'Congo DR': 'congo-dr', 'South Africa': 'south-africa',
    'Cape Verde Islands': 'cape-verde', 'New Zealand': 'new-zealand', 'Panama': 'panama',
    'Haiti': 'haiti', 'Bosnia & Herzegovina': 'bosnia', 'Czech Republic': 'czech-republic',
    'Jordan': 'jordan', 'Iraq': 'iraq', 'Uzbekistan': 'uzbekistan', 'Curaçao': 'curacao',
  };
  return map[name] || name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

export default function WorldCupMatch() {
  const { matchId: rawMatchId } = useParams();
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const wcBase = currentLang === 'tr' ? `/${currentLang}/dunya-kupasi-2026` : `/${currentLang}/world-cup-2026`;
  const isTR = currentLang === 'tr';

  // Extract numeric ID from slug (e.g. "meksika-vs-guney-afrika-1489369" → "1489369")
  const matchId = extractWCMatchId(rawMatchId);

  useEffect(() => {
    if (!matchId) return;
    fetch(`${API_BASE}/api/worldcup/match/${matchId}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [matchId]);

  if (loading) return <LoadingPage />;
  if (!data?.match) return <div className="container mx-auto px-4 py-8 text-slate-400">Maç bulunamadı</div>;

  const { match: m, group, venue, seo } = data;
  const date = m.date ? new Date(m.date) : null;
  const isFinished = m.status === 'Match Finished';
  const isLive = m.status_short === 'LIVE';
  const homeWon = isFinished && m.score_home > m.score_away;

  const groupLetterMatch = group?.name?.match(/Group\s*([A-L])/i);
  const groupLetter = groupLetterMatch ? groupLetterMatch[1].toLowerCase() : (group?.name?.replace(/[^a-zA-Z]/g, '') || '').toLowerCase();

  const LOCALE_MAP = { tr: 'tr-TR', en: 'en-US', es: 'es-ES', de: 'de-DE', fr: 'fr-FR', pt: 'pt-BR', it: 'it-IT', nl: 'nl-NL', ru: 'ru-RU', uk: 'uk-UA', ar: 'ar-SA', id: 'id-ID' };
  const locale = LOCALE_MAP[currentLang] || 'en-US';

  return (
    <div data-testid="worldcup-match">
      <WCSubmenu />
      <SEOHead
        title={seo?.title || `${translateCountryName(m.home_name, currentLang)} vs ${translateCountryName(m.away_name, currentLang)} | ODDSIS`}
        description={seo?.description}
        canonical={seo?.canonical}
        hreflang={seo?.hreflang}
        jsonLd={seo?.jsonld}
      />

      {/* Scoreboard */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #020617 0%, #0a1628 30%, #1a472a 70%, #0a1628 100%)' }}>
        <div className="relative z-10 container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-6">
            <Link to={`/${currentLang}`} className="hover:text-slate-200">{t('nav.home')}</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={wcBase} className="hover:text-amber-300 text-amber-400/70">{t('wc.breadcrumb')}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-300">{translateCountryName(m.home_name, currentLang)} vs {translateCountryName(m.away_name, currentLang)}</span>
          </div>

          {/* League + Round info */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <img src="https://media.api-sports.io/football/leagues/1.png" alt="" className="w-5 h-5" />
            <span className="text-xs text-white/60 uppercase tracking-widest">{t('wc.title')}</span>
            <span className="text-xs text-amber-400/60">- {m.round.replace('Group Stage - ', t('wc.groupStagePrefix'))}</span>
          </div>

          {/* Teams + Score */}
          <div className="flex items-center justify-center gap-8 md:gap-16">
            <div className="text-center flex-1">
              <img src={m.home_logo} alt={m.home_name} className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-3 drop-shadow-xl" />
              <p className={`text-base md:text-lg font-bold ${m.home_name === 'Türkiye' ? 'text-red-400' : homeWon ? 'text-white' : 'text-white/70'}`}>{translateCountryName(m.home_name, currentLang)}</p>
            </div>

            <div className="text-center shrink-0">
              {m.score_home != null ? (
                <div className="flex items-center gap-4">
                  <span className={`text-5xl md:text-6xl font-black tabular-nums ${homeWon ? 'text-white' : 'text-white/50'}`}>{m.score_home}</span>
                  <span className="text-3xl text-white/20">-</span>
                  <span className={`text-5xl md:text-6xl font-black tabular-nums ${!homeWon ? 'text-white' : 'text-white/50'}`}>{m.score_away}</span>
                </div>
              ) : (
                <div>
                  <p className="text-3xl font-bold text-white/30">VS</p>
                  {date && (
                    <div className="mt-2">
                      <p className="text-sm text-amber-400">{date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                      <p className="text-xs text-white/40">{date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  )}
                </div>
              )}
              <p className="text-[10px] text-white/40 uppercase tracking-widest mt-2">{isFinished ? (isTR ? 'BİTTİ' : 'FINAL') : isLive ? (isTR ? 'CANLI' : 'LIVE') : (isTR ? 'Henüz başlamadı' : 'Not started')}</p>
            </div>

            <div className="text-center flex-1">
              <img src={m.away_logo} alt={m.away_name} className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-3 drop-shadow-xl" />
              <p className={`text-base md:text-lg font-bold ${m.away_name === 'Türkiye' ? 'text-red-400' : !homeWon && isFinished ? 'text-white' : 'text-white/70'}`}>{translateCountryName(m.away_name, currentLang)}</p>
            </div>
          </div>

          {/* Venue */}
          <div className="flex items-center justify-center gap-4 mt-6 text-xs text-white/40">
            {venue.name && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{venue.name}, {venue.city}</span>}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-5xl py-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Group Context */}
          {group && (
            <div className="bg-slate-900/60 border border-white/5 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-white"><Trophy className="w-4 h-4 inline mr-2 text-amber-400" />{translateGroupName(group.name, currentLang)}</h3>
                <Link to={`${wcBase}/grup/${groupLetter}`} className="text-[10px] text-amber-400 hover:text-amber-300">Detay</Link>
              </div>
              {group.standings?.map((s, i) => {
                const active = s.team_name === m.home_name || s.team_name === m.away_name;
                return (
                  <Link key={i} to={`${wcBase}/takim/${_teamSlug(s.team_name)}`} className={`flex items-center gap-2 py-1.5 hover:text-amber-300 transition-colors ${active ? 'text-white font-medium' : 'text-slate-500'}`}>
                    <span className="w-4 text-xs text-right">{s.rank || i+1}</span>
                    <img src={s.team_logo} alt="" className="w-4 h-4" />
                    <span className="text-xs flex-1">{translateCountryName(s.team_name, currentLang)}</span>
                    <span className="text-xs tabular-nums">{s.points} P</span>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Match Info */}
          <div className="bg-slate-900/60 border border-white/5 rounded-xl p-5">
            <h3 className="text-sm font-bold text-white mb-3"><Calendar className="w-4 h-4 inline mr-2 text-amber-400" />{isTR ? 'Maç Bilgileri' : 'Match Info'}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">{isTR ? 'Turnuva' : 'Tournament'}</span><span className="text-slate-300">{isTR ? 'FIFA Dünya Kupası 2026' : 'FIFA World Cup 2026'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">{isTR ? 'Tur' : 'Round'}</span><span className="text-slate-300">{m.round.replace('Group Stage - ', t('wc.groupStagePrefix'))}</span></div>
              {date && <div className="flex justify-between"><span className="text-slate-500">{isTR ? 'Tarih' : 'Date'}</span><span className="text-slate-300">{date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })}</span></div>}
              {date && <div className="flex justify-between"><span className="text-slate-500">{isTR ? 'Saat' : 'Time'}</span><span className="text-slate-300">{date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</span></div>}
              <div className="flex justify-between"><span className="text-slate-500">{isTR ? 'Stadyum' : 'Stadium'}</span><span className="text-slate-300">{venue.name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">{isTR ? 'Şehir' : 'City'}</span><span className="text-slate-300">{venue.city}</span></div>
            </div>
          </div>
        </div>

        {/* AI Prediction */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-amber-500/20 rounded-xl p-6 shadow-[0_0_15px_rgba(251,191,36,0.05)]">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <h3 className="text-sm font-bold text-white">{isTR ? 'AI Maç Tahmini' : 'AI Match Prediction'}</h3>
            <span className="text-[9px] bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">{isTR ? 'YAKINDA' : 'COMING SOON'}</span>
          </div>
          <p className="text-sm text-slate-400">{isTR ? 'Bu maç için AI destekli tahmin ve analiz turnuva yaklaştıkça eklenecektir.' : 'AI-powered prediction and analysis for this match will be added as the tournament approaches.'}</p>
        </div>

        {/* Odds Section */}
        {data.odds_count > 0 ? (
          <div className="space-y-4" data-testid="wc-odds-section">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-400" />
              <h2 className="text-lg font-bold text-white uppercase tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{isTR ? 'Bahis Oranları' : 'Betting Odds'}</h2>
              <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">{data.odds_count} {isTR ? 'oran' : 'odds'}</span>
            </div>
            {Object.entries(data.odds).map(([marketId, odds]) => {
              const marketLabels = isTR ? MARKET_LABELS_TR : { '1x2': '1X2', 'double-chance': 'Double Chance', 'btts': 'BTTS', 'over-under-2.5': 'Over/Under 2.5', 'over-under-1.5': 'Over/Under 1.5', 'over-under-3.5': 'Over/Under 3.5', 'draw-no-bet': 'Draw No Bet', 'first-half-1x2': 'First Half 1X2' };
              const homeTr = translateCountryName(m.home_name, currentLang);
              const awayTr = translateCountryName(m.away_name, currentLang);
              const selectionLabels = isTR
                ? { home: homeTr, draw: 'Beraberlik', away: awayTr, yes: 'Var', no: 'Yok', over: 'Üst', under: 'Alt', home_draw: '1X', home_away: '12', draw_away: 'X2' }
                : { home: homeTr, draw: 'Draw', away: awayTr, yes: 'Yes', no: 'No', over: 'Over', under: 'Under', home_draw: '1X', home_away: '12', draw_away: 'X2' };
              const grouped = {};
              odds.forEach(o => {
                const key = o.bookmaker_id;
                if (!grouped[key]) grouped[key] = {};
                grouped[key][o.selection] = o;
              });
              const selections = [...new Set(odds.map(o => o.selection))];
              return (
                <div key={marketId} className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                  <div className="px-4 py-2 border-b border-slate-800 bg-slate-800/50">
                    <span className="text-xs font-bold text-amber-400 uppercase">{marketLabels[marketId] || marketId}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-800/50">
                          <th className="px-4 py-2 text-left text-xs text-slate-500 font-medium">{isTR ? 'Bahisçi' : 'Bookmaker'}</th>
                          {selections.map(s => (
                            <th key={s} className="px-3 py-2 text-center text-xs text-slate-500 font-medium">{selectionLabels[s] || s}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(grouped).map(([bkm, sels]) => (
                          <tr key={bkm} className="border-b border-slate-800/30 hover:bg-slate-800/30">
                            <td className="px-4 py-2 text-xs text-slate-300 font-medium capitalize">{bkm.replace(/-/g, ' ')}</td>
                            {selections.map(s => {
                              const o = sels[s];
                              return (
                                <td key={s} className="px-3 py-2 text-center">
                                  {o ? (
                                    <span className={`text-sm font-bold tabular-nums ${o.is_best ? 'text-emerald-400' : 'text-slate-200'}`}>{Number(o.odds_value).toFixed(2)}</span>
                                  ) : (
                                    <span className="text-slate-700">-</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-slate-900/30 border border-dashed border-slate-700/50 rounded-xl p-6 text-center" data-testid="wc-odds-pending">
            <BarChart3 className="w-6 h-6 text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-500">{isTR ? 'Bahis oranları maça yaklaştıkça burada gösterilecektir.' : 'Betting odds will be displayed here as the match approaches.'}</p>
            <p className="text-xs text-slate-600 mt-1">{isTR ? '30+ bahisçide canlı oran karşılaştırması' : 'Live odds comparison across 30+ bookmakers'}</p>
          </div>
        )}

        {/* ═══ MATCH EVENTS (Goals, Cards) ═══ */}
        {data.events?.length > 0 && (
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid="match-events">
            <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" />
              {isTR ? 'Maç Olayları' : 'Match Events'}
            </h3>
            <div className="space-y-2">
              {data.events.map((ev, i) => {
                const isGoal = ev.type === 'Goal';
                const isCard = ev.type === 'Card';
                const isSub = ev.type === 'subst';
                const isHome = ev.team_name === m.home_name;
                return (
                  <div key={i} className={`flex items-center gap-3 py-1.5 ${isHome ? '' : 'flex-row-reverse text-right'}`}>
                    <span className="text-xs text-slate-500 tabular-nums w-8 shrink-0 text-center">{ev.time}'</span>
                    <div className={`flex items-center gap-2 ${isHome ? '' : 'flex-row-reverse'}`}>
                      {isGoal && <span className="text-base">⚽</span>}
                      {isCard && ev.detail === 'Yellow Card' && <span className="w-3 h-4 bg-yellow-400 rounded-sm inline-block" />}
                      {isCard && ev.detail === 'Red Card' && <span className="w-3 h-4 bg-red-500 rounded-sm inline-block" />}
                      {isSub && <span className="text-xs">🔄</span>}
                      <span className={`text-xs ${isGoal ? 'text-white font-semibold' : 'text-slate-400'}`}>{ev.player}</span>
                      {ev.assist && isGoal && <span className="text-[10px] text-slate-600">({isTR ? 'asist' : 'assist'}: {ev.assist})</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══ MATCH STATISTICS ═══ */}
        {data.statistics?.length === 2 && (
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid="match-statistics">
            <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-cyan-400" />
              {isTR ? 'Maç İstatistikleri' : 'Match Statistics'}
            </h3>
            <div className="space-y-3">
              {(() => {
                const home = data.statistics[0];
                const away = data.statistics[1];
                const statKeys = ['Ball Possession', 'Total Shots', 'Shots on Goal', 'Corner Kicks', 'Fouls', 'Offsides', 'Yellow Cards', 'Red Cards', 'Passes %'];
                const trLabels = { 'Ball Possession': 'Topa Sahip Olma', 'Total Shots': 'Toplam Şut', 'Shots on Goal': 'İsabetli Şut', 'Corner Kicks': 'Korner', 'Fouls': 'Faul', 'Offsides': 'Ofsayt', 'Yellow Cards': 'Sarı Kart', 'Red Cards': 'Kırmızı Kart', 'Passes %': 'Pas Başarısı' };
                return statKeys.filter(k => home.stats[k] != null || away.stats[k] != null).map(key => {
                  const hVal = home.stats[key] ?? 0;
                  const aVal = away.stats[key] ?? 0;
                  const hNum = typeof hVal === 'string' ? parseInt(hVal) || 0 : hVal;
                  const aNum = typeof aVal === 'string' ? parseInt(aVal) || 0 : aVal;
                  const total = hNum + aNum || 1;
                  const hPct = Math.round(hNum / total * 100);
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-slate-200 tabular-nums w-12 text-center">{hVal}</span>
                        <span className="text-slate-500 text-center flex-1">{isTR ? (trLabels[key] || key) : key}</span>
                        <span className="text-slate-200 tabular-nums w-12 text-center">{aVal}</span>
                      </div>
                      <div className="flex h-1.5 rounded-full overflow-hidden bg-slate-800 gap-px">
                        <div className="bg-cyan-500/60 rounded-l-full" style={{ width: `${hPct}%` }} />
                        <div className="bg-rose-500/60 rounded-r-full" style={{ width: `${100 - hPct}%` }} />
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        {/* ═══ LINEUPS ═══ */}
        {data.lineups?.length === 2 && (
          <div className="grid grid-cols-2 gap-4" data-testid="match-lineups">
            {data.lineups.map((lineup, i) => (
              <div key={i} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-white">{translateCountryName(lineup.team_name, currentLang)}</span>
                  <span className="text-[10px] text-amber-400 font-mono">{lineup.formation}</span>
                </div>
                <div className="space-y-1">
                  {lineup.startXI?.map((player, j) => (
                    <div key={j} className="flex items-center gap-2 text-xs text-slate-400">
                      <span className="w-4 text-[10px] text-slate-600 text-right">{j + 1}</span>
                      <span>{player}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Placeholder for not-started matches */}
        {!data.events?.length && !data.statistics?.length && !isFinished && (
          <div className="bg-slate-900/30 border border-dashed border-slate-700/50 rounded-xl p-6 text-center">
            <Globe className="w-6 h-6 text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-500">{isTR ? 'Maç istatistikleri maç başladıktan sonra burada gösterilecektir.' : 'Match statistics will appear here after the match starts.'}</p>
          </div>
        )}

        <WCInternalLinks currentPage={`match-${matchId}`} lang={currentLang} />
      </div>
    </div>
  );
}
